/* 
The ImageEditor component is a React-based image cropping and editing tool designed to 
provide an intuitive and efficient user experience.

Props passed to Cropper:
1. image: The first image URL from imageUrls.
2. crop: The current cropping coordinates.
3. rotation: The current rotation angle.
4. zoom: The current zoom level.
5. aspect: The aspect ratio (default is 4:3).
6. onCropChange: Updates the crop state.
7. onRotationChange: Updates the rotation state.
8. onCropComplete: Handles cropping completion.
9. onZoomChange: Updates the zoom state.
10. cropSize: Custom cropping dimensions (if provided via customCrop).

Zoom Slider: A range slider to adjust the zoom level. Updates the zoom state on change.

Rotation Slider (Optional):  Displayed only if allowImageRotation is true. A range slider to adjust the rotation angle. Updates the rotation state on change.

Action Buttons:
1. Cancel Button: Calls the onClose function to close the editor.
2. Save Button: Calls the croppedImageBuilder function to save the cropped image.

*/

import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { ILogoUploadingLayoutProps, PixelCrop } from "./ImageEditor.interface";
import getCroppedImg, { cleanupImageUrls, filesToImageUrls } from "./ImageEditor.service";
import toast from "react-hot-toast";
import "./ImageEditor.css";


const ImageEditor = (props: ILogoUploadingLayoutProps) => {
  
  const { selectedFiles, extractCroppedImage, handleClose, maxFileSize, customCrop, allowImageRotation } = props;
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0 });// State to manage cropping coordinates
  const [rotation, setRotation] = useState(0); // State to manage image rotation angle
  const [zoom, setZoom] = useState(1);  // State to manage zoom level
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null); // State to store the cropped area's pixel dimensions
  const [croppedImage, setCroppedImage] = useState<string | null>(null);// State to store the cropped image URL
  const [imageUrls, setImageUrls] = useState<string[]>([]);// State to store the generated image URLs from the selected files

  /*
  Effect: A useEffect hook is used to generate image URLs from the selectedFiles prop.
  Cleanup: The cleanupImageUrls utility is called to revoke object URLs when the component unmounts.
  */
  useEffect(() => {
    const urls = filesToImageUrls(selectedFiles);
    setImageUrls(urls);

    return () => {
      cleanupImageUrls(urls);
    };
  }, [selectedFiles]);


  // function that captures the natural dimensions of the loaded image
  const onImageLoad = (mediaSize: { width: number; height: number }) => {
    setImageDimensions({
      width: mediaSize.width,
      height: mediaSize.height
    });
  };

  /*
  Effect: Another useEffect hook validates the size of the first file in selectedFiles against the maxFileSize prop.
  Behavior:
      1. If the file size exceeds the limit:
      2. extractCroppedImage is called with null.
      3. The editor is closed using handleClose.
      4. An error message is displayed using toast.
  */

  useEffect(() => {
    if (maxFileSize && (selectedFiles[0].size > maxFileSize)) {
      extractCroppedImage(null)
      handleClose && handleClose();
      toast.error("File size exceeding")
    }
  }, [maxFileSize]);

  //Callback triggered when cropping is complete
  const onCropComplete = (_: any, croppedAreaPixels: PixelCrop) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };



  /*Function: croppedImageBuilder generates the cropped image.
  Steps:
    1. Validates if croppedAreaPixels and imageUrls are available.
    2. Calls the getCroppedImg utility to crop and rotate the image.
    3. Converts the cropped image into a File object.
    4. Passes the cropped file to the extractCroppedImage callback.
    5. Closes the editor using handleClose. */
  const croppedImageBuilder = async () => {
    try {
      if (!croppedAreaPixels) {
        console.error("No area selected for cropping");
        return;
      }
      const fileToCrop = Array.isArray(imageUrls) ? imageUrls[0] : imageUrls;

      if (!fileToCrop) {
        console.error("No file available for cropping");
        return;
      }

      const image = await getCroppedImg(fileToCrop, croppedAreaPixels, rotation);
      setCroppedImage(image);

      // Convert to File object if needed
      if (extractCroppedImage) {
        const response = await fetch(image);
        const blob = await response.blob();
        const croppedFile = new File([blob], selectedFiles[0].name, {
          type: "image/jpeg",
        });
        extractCroppedImage([croppedFile]);
      }
      handleClose && handleClose();
    } catch (e) {
      console.error("Cropping failed:", e);
    }
  };

  /*
  Function: onClose handles the closure of the editor.
    Steps:
      1. Revokes the object URL of the cropped image (if available).
      2. Resets the croppedImage state.
      3. Calls the handleClose callback.
  */
  const onClose = () => {
    if (croppedImage) {
      URL.revokeObjectURL(croppedImage);
    }
    setCroppedImage(null);
    handleClose && handleClose();
  };

  return (
    <div className="image-editor-container">
      <div className="container cropper-container">
        <div className="position-absolute drag-img-div d-flex gap-2 align-items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-arrows-move"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708zM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10M.146 8.354a.5.5 0 0 1 0-.708l2-2a.5.5 0 1 1 .708.708L1.707 7.5H5.5a.5.5 0 0 1 0 1H1.707l1.147 1.146a.5.5 0 0 1-.708.708zM10 8a.5.5 0 0 1 .5-.5h3.793l-1.147-1.146a.5.5 0 0 1 .708-.708l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L14.293 8.5H10.5A.5.5 0 0 1 10 8"
            />
          </svg>
          <span>Drag to reposition</span>
        </div>
        <Cropper
          image={
            Array.isArray(imageUrls) ? imageUrls[0] : imageUrls
          }
          crop={crop}
          rotation={rotation}
          zoom={zoom}
          onCropChange={setCrop}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          cropSize={customCrop ?? imageDimensions}
          onMediaLoaded={onImageLoad}
        />
      </div>

      <div className="container mt-3">
        <div className="container mt-3">
          <div className="row mb-3">
            <div className="col">


              <div className="PB-range-slider-div">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-dash-lg"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M2 8a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11A.5.5 0 0 1 2 8"
                  />
                </svg>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  className="PB-range-slider"
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  id="myRange"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-plus-lg"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"
                  />
                </svg>
              </div>
            </div>

            {
              allowImageRotation ?
                <div className="col-md-6">
                  <label htmlFor="rotationSlider" className="form-label">
                    Rotation
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    id="rotationSlider"
                    min={0}
                    max={360}
                    step={1}
                    value={rotation}
                    onChange={(e) => setRotation(parseInt(e.target.value))}
                  />
                  <div className="d-flex justify-content-between">
                    <small>0°</small>
                    <small>360°</small>
                  </div>
                </div> : null
            }
          </div>

          <div className="my-2 bg-white rounded d-flex justify-content-end">
            <button className="sph-btn sph-shift me-2" onClick={onClose}>Cancel</button>
            <button className="sph-btn me-2" onClick={croppedImageBuilder}>Save</button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ImageEditor;
